import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventBySlug } from '../../services/apiClient';
import type { Event } from '../../types';
import { formatDateRange, formatDate } from '../../lib/formatting';
import { StatusBadge } from '../../components/ui/StatusBadge';
import styles from './EventDetailPage.module.css';

export function EventDetailPage() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!eventSlug) return;
    getEventBySlug(eventSlug).then((r) => {
      if (r.data) setEvent(r.data);
      else setNotFound(true);
      setLoading(false);
    });
  }, [eventSlug]);

  if (loading) return <div className={styles.loading}>Loading…</div>;
  if (notFound || !event) {
    return (
      <div className={styles.notFound}>
        <h2>Event not found</h2>
        <Link to="/events">Back to Events</Link>
      </div>
    );
  }

  const deadlinePassed = new Date(event.registration_deadline) < new Date();

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className="container">
          <Link to="/events" className={styles.back}>← All Events</Link>
          <div className={styles.titleRow}>
            <div>
              <div className={styles.meta}>
                <span className={styles.type}>{event.type}</span>
                <StatusBadge label={event.status} />
              </div>
              <h1 className={styles.title}>{event.title}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>
          <div className={styles.main}>
            <p className={styles.description}>{event.description}</p>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.infoCard}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Date</span>
                <span className={styles.infoValue}>
                  {formatDateRange(event.start_date, event.end_date)}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Location</span>
                <span className={styles.infoValue}>{event.location}</span>
              </div>
              {event.max_entries > 0 && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Max Entries</span>
                  <span className={styles.infoValue}>{event.max_entries}</span>
                </div>
              )}
              {event.registration_open && (
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Deadline</span>
                  <span className={styles.infoValue}>
                    {formatDate(event.registration_deadline)}
                  </span>
                </div>
              )}

              <div className={styles.regSection}>
                {event.registration_open && !deadlinePassed ? (
                  <Link to={`/register/${event.slug}`} className={styles.registerBtn}>
                    Register for this Event
                  </Link>
                ) : (
                  <p className={styles.regClosed}>
                    {deadlinePassed ? 'Registration deadline has passed' : 'Registration is not currently open'}
                  </p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
